import { getMessageDate, getLogActorDisplayName } from "./timelineUtils";

export const ACTIVITY_TZ = "America/Araguaina";

const STATUS_LABELS = new Set([
  "Pendente",
  "Em Atendimento",
  "Concluída",
  "Contato Futuro",
  "Perdida",
  "Cancelada",
]);

export const isRequestStatusLabel = (label) => STATUS_LABELS.has(String(label || "").trim());

const stripHtml = (html) =>
  typeof html === "string" ? html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";

export const cutLogTail = (text) =>
  String(text || "")
    .split(/\s+(?:Confiança|Motivo)\s*:/i)[0]
    .trim()
    .replace(/\.\s*$/, "");

export const formatActivityClock = (date) => {
  if (!date) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: ACTIVITY_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
};

export const formatActivityFull = (date) => {
  if (!date) return "";
  const datePart = new Intl.DateTimeFormat("pt-BR", {
    timeZone: ACTIVITY_TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
  return `${datePart} às ${formatActivityClock(date)}`;
};

export const formatActivityRelative = (date, now = new Date()) => {
  if (!date) return "";
  const diffMs = date.getTime() - now.getTime();
  const abs = Math.abs(diffMs);
  const minutes = Math.round(abs / 60000);
  if (minutes < 1) return "agora";
  const rtf = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });
  if (minutes < 60) return rtf.format(Math.round(diffMs / 60000), "minute");
  const hours = Math.round(diffMs / 3600000);
  if (Math.abs(hours) < 24) return rtf.format(hours, "hour");
  const days = Math.round(diffMs / 86400000);
  return rtf.format(days, "day");
};

export const activityTimeTitle = (date) => {
  if (!date) return "";
  const full = formatActivityFull(date);
  const relative = formatActivityRelative(date);
  return relative ? `${full} (${relative})` : full;
};

export const formatActivityClockRange = (messages) => {
  const dates = (messages || []).map(getMessageDate).filter(Boolean);
  if (!dates.length) return "";
  const first = formatActivityClock(dates[0]);
  const last = formatActivityClock(dates[dates.length - 1]);
  if (!last || first === last) return first;
  return `${first}–${last}`;
};

const changeLabel = (change, side) => {
  const value = side === "from" ? change?.fromLabel : change?.toLabel;
  return value != null ? String(value).trim() : "";
};

export const getActivityChanges = (message) => {
  const stored = message?.logMeta?.changes;
  if (Array.isArray(stored) && stored.length) return stored;
  const from = message?.logMeta?.from?.label;
  const to = message?.logMeta?.to?.label;
  if (from && to && from !== to) {
    return [{ field: message.logMeta?.field || message.logMeta?.code, fromLabel: from, toLabel: to }];
  }
  return [];
};

const messagePlain = (message) =>
  message?.textPlain || stripHtml(message?.text || message?.logMeta?.summary || "");

/** O último " para " separa origem e destino, mesmo quando o nome contém "para". */
export const parseDePara = (text) => {
  const cut = cutLogTail(text);
  const match = cut.match(/\bDe\s+(.+)\s+para\s+(.+)$/i);
  if (!match) return null;
  const from = match[1].trim();
  const to = match[2].trim().replace(/\.$/, "");
  if (!from || !to) return null;
  return { from, to };
};

export const extractMotivo = (message) => {
  const plain = messagePlain(message);
  const stored = message?.logMeta?.detail?.reasoning;
  if (stored) return String(stored).trim();
  const match = plain.match(/Motivo:\s*(.+)$/i);
  return match ? match[1].trim().replace(/\.$/, "") : "";
};

const rewritePlainSummary = (plain) => {
  const text = cutLogTail(plain);
  if (!text) return "";
  const dePara = parseDePara(text);
  if (/tipo\/subtipos|tipo alterado|subtipo/i.test(text) && dePara) {
    return `Alterou o tipo de ${dePara.from} para ${dePara.to}`;
  }
  if (/^Status alterado\b/i.test(text)) return "Alterou o status";
  if (/^Potencial alterado\b/i.test(text) && dePara) return `Alterou o potencial de ${dePara.from} para ${dePara.to}`;
  if (/^Projeto alterado\b/i.test(text) && dePara) return `Alterou o projeto de ${dePara.from} para ${dePara.to}`;
  if (/^Canal alterado\b/i.test(text) && dePara) return `Alterou o canal de ${dePara.from} para ${dePara.to}`;
  if (/^Próximo contato alterado\b/i.test(text) && dePara) return `Alterou o próximo contato de ${dePara.from} para ${dePara.to}`;
  if (/^Tarefa criada:/i.test(text)) return text.replace(/^Tarefa criada:\s*/i, "Criou a tarefa ");
  if (/^Tarefa vinculada criada:/i.test(text)) {
    const name = text.replace(/^Tarefa vinculada criada:\s*/i, "").replace(/\s*\(a partir de[\s\S]*$/i, "");
    const origin = text.match(/\(a partir de\s+(.+)\)\s*$/i);
    return origin
      ? `Criou a tarefa vinculada ${name.trim()} a partir de ${origin[1].trim()}`
      : `Criou a tarefa vinculada ${name}`.trim();
  }
  if (/^Uma tarefa vinculada foi concluída/i.test(text)) return "Concluiu uma tarefa vinculada";
  const restarted = text.match(/^Tarefa\s+(.+?)\s+reiniciada$/i);
  if (restarted) return `Reiniciou a tarefa ${restarted[1]}`;
  const done = text.match(/^Tarefa\s+(.+?)\s+concluída/i);
  if (done) return `Concluiu a tarefa ${done[1]}`;
  if (/^Cadastrada automaticamente via\s+/i.test(text)) {
    return text.replace(/^Cadastrada automaticamente via\s+/i, "Abriu pelo ");
  }
  if (/^Cadastrada por:/i.test(text)) return "Cadastrou a solicitação";
  if (/^Solicitação fixada/i.test(text)) return "Fixou a solicitação";
  if (/^Solicitação desfixada/i.test(text)) return "Desfixou a solicitação";
  return text;
};

const GENERIC_SUMMARIES = new Set([
  "classificou o tipo da solicitação",
  "aceitou a sugestão de tipo da ia",
]);

export const getActivitySentence = (message) => getActivityPresentation(message).headline;

const tidy = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .replace(/([.!?])([^\s])/g, "$1 $2")
    .trim();

export const clampText = (value, max = 92) => {
  const clean = tidy(value);
  if (clean.length <= max) return { short: clean, rest: "" };
  const slice = clean.slice(0, max);
  const cut = slice.lastIndexOf(" ");
  const short = (cut > 48 ? slice.slice(0, cut) : slice).trim();
  return { short: `${short}…`, rest: clean };
};

const splitOrigin = (body) => {
  const match = tidy(body).match(/^(.*)\s*\(a partir de\s+(.+)\)\s*$/i);
  if (!match) return { name: tidy(body), origin: "" };
  return { name: tidy(match[1]), origin: tidy(match[2]) };
};

const shapeTaskPhrase = (text) => {
  const raw = tidy(text);
  if (!raw) return null;
  let verb = "";
  let name = "";
  let origin = "";
  let note = "";

  const linked = raw.match(/^Tarefa vinculada criada:\s*(.+)$/i);
  const created = raw.match(/^Tarefa criada:\s*(.+)$/i);
  const createdVerb = raw.match(/^Criou a tarefa vinculada\s+(.+)$/i);
  const createdSimple = raw.match(/^Criou a tarefa\s+(.+)$/i);
  const renamed = raw.match(/^Tarefa atualizada de\s+(.+)\s+para\s+(.+)$/i);
  const action = raw.match(/^Tarefa\s+(.+)\s+(reiniciada|concluída|concluida|apagada)(?:\s*[—-]\s*(.+))?$/i);
  const restarted = raw.match(/^Reiniciou a tarefa\s+(.+)$/i);
  const finished = raw.match(/^Concluiu a tarefa\s+(.+)$/i);
  const moved = raw.match(/^Tarefa\s+(.+)\s+voltou para\s+(.+)$/i);

  if (linked) {
    verb = "Criou a tarefa vinculada";
    ({ name, origin } = splitOrigin(linked[1]));
  } else if (created) {
    verb = "Criou a tarefa";
    ({ name, origin } = splitOrigin(created[1]));
  } else if (createdVerb) {
    verb = "Criou a tarefa vinculada";
    const parts = createdVerb[1].match(/^(.*)\s+a partir de\s+(.+)$/i);
    if (parts) {
      name = tidy(parts[1]);
      origin = tidy(parts[2]);
    } else {
      ({ name, origin } = splitOrigin(createdVerb[1]));
    }
  } else if (createdSimple && !/^vinculada\b/i.test(createdSimple[1])) {
    verb = "Criou a tarefa";
    name = tidy(createdSimple[1]);
  } else if (renamed) {
    verb = "Renomeou a tarefa";
    name = tidy(renamed[2]);
    note = tidy(renamed[1]);
  } else if (action) {
    const kind = action[2].toLowerCase();
    verb = kind.startsWith("conclu") ? "Concluiu a tarefa" : kind === "apagada" ? "Apagou a tarefa" : "Reiniciou a tarefa";
    name = tidy(action[1]);
    note = tidy(action[3] || "");
  } else if (restarted) {
    verb = "Reiniciou a tarefa";
    name = tidy(restarted[1]);
  } else if (finished) {
    verb = "Concluiu a tarefa";
    name = tidy(finished[1]);
  } else if (moved) {
    verb = "Reabriu a tarefa";
    name = tidy(moved[1]);
    note = `Voltou para ${tidy(moved[2])}`;
  } else {
    return null;
  }

  const clamped = clampText(name, 88);
  const originClamped = origin ? clampText(origin, 72) : { short: "", rest: "" };
  const extraParts = [clamped.rest, note, originClamped.rest].filter(Boolean);
  return {
    headline: originClamped.short ? `${verb} ${clamped.short}` : `${verb} ${clamped.short}`.trim(),
    origin: originClamped.short ? `A partir de ${originClamped.short}` : "",
    extra: extraParts.join("\n"),
  };
};

export const getActivityPresentation = (message) => {
  const plain = messagePlain(message);
  const rewritten = rewritePlainSummary(plain);
  const summary = String(message?.logMeta?.summary || "").trim();
  let headline = rewritten || summary;
  if (/^Status alterado\b/i.test(summary) || /^Status alterado\b/i.test(plain)) headline = "Alterou o status";
  else if (summary && !GENERIC_SUMMARIES.has(summary.toLowerCase()) && !/^Classificou como\b/i.test(summary)) {
    headline = summary;
  } else if (/^Classificou como\b/i.test(summary) && /Alterou o tipo de /i.test(rewritten)) {
    headline = rewritten;
  }

  const shaped = shapeTaskPhrase(plain) || shapeTaskPhrase(summary) || shapeTaskPhrase(headline);
  const motivo = extractMotivo(message);
  if (!shaped) {
    return { headline, origin: "", extra: "", motivo };
  }
  return { ...shaped, motivo };
};

export const isLegacyXproLog = (message) => message?.logMeta?.source === "xpro2";

const TASK_VERBS = /^(Criou a tarefa vinculada|Criou a tarefa|Reiniciou a tarefa|Concluiu a tarefa|Apagou a tarefa|Renomeou a tarefa|Reabriu a tarefa)\b/i;

const normalizeTaskText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/#\d+\s*/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const scoreTaskTitle = (task, title) => {
  const name = normalizeTaskText(`${task?.title || ""} ${task?.description || ""}`);
  const needle = normalizeTaskText(title);
  if (!name || needle.length < 12) return 0;
  if (name === needle) return 1;
  const slice = needle.slice(0, 48);
  if (name.startsWith(slice) || needle.startsWith(name.slice(0, 48))) return 0.92;
  const words = needle.split(" ").filter((word) => word.length > 3);
  if (!words.length) return 0;
  const hits = words.filter((word) => name.includes(word)).length;
  return hits / words.length;
};

export const pickActivityTask = (cards, { cardId, title } = {}) => {
  const list = Array.isArray(cards) ? cards : [];
  const agrees = (task) => !title || normalizeTaskText(title).length < 12 || scoreTaskTitle(task, title) >= 0.45;
  if (cardId) {
    const byId = list.find((task) => Number(task.id) === Number(cardId));
    if (byId && agrees(byId)) return byId;
  }
  const needle = normalizeTaskText(title);
  if (!needle || /^(uma )?tarefa( vinculada)?$/.test(needle)) return null;
  let best = null;
  let bestScore = 0.45;
  list.forEach((task) => {
    const score = scoreTaskTitle(task, title);
    if (score > bestScore) {
      best = task;
      bestScore = score;
    }
  });
  return best;
};

export const getTaskLogRef = (message) => {
  const presentation = getActivityPresentation(message);
  const plain = messagePlain(message);
  const entity = message?.logMeta?.entity;
  const isTask =
    entity?.type === "task" ||
    message?.logMeta?.code === "team_task" ||
    TASK_VERBS.test(presentation.headline) ||
    /^Tarefa\b/i.test(plain);
  if (!isTask) return null;
  const fromEntity = Number(entity?.id);
  const hash = String(plain).match(/#(\d+)/);
  const cardId =
    Number.isFinite(fromEntity) && fromEntity > 0
      ? fromEntity
      : hash
        ? Number(hash[1])
        : null;
  const title = presentation.headline
    .replace(TASK_VERBS, "")
    .replace(/^#\d+\s*/, "")
    .replace(/…$/, "")
    .trim();
  const searchTitle = tidy(plain)
    .replace(/^Tarefa vinculada criada:\s*/i, "")
    .replace(/^Tarefa criada:\s*/i, "")
    .replace(/^Criou a tarefa vinculada\s+/i, "")
    .replace(/^Criou a tarefa\s+/i, "")
    .replace(/^Reiniciou a tarefa\s+/i, "")
    .replace(/^Concluiu a tarefa\s+/i, "")
    .replace(/^Apagou a tarefa\s+/i, "")
    .replace(/^Renomeou a tarefa\s+/i, "")
    .replace(/^Reabriu a tarefa\s+/i, "")
    .replace(/\s*\(a partir de\s+[\s\S]+$/i, "")
    .replace(/\s+(reiniciada|concluída|concluida|apagada)\b[\s\S]*$/i, "")
    .replace(/^Tarefa\s+/i, "")
    .replace(/^#\d+\s*/, "")
    .trim();
  return {
    cardId: cardId || null,
    title: searchTitle.length > title.length ? searchTitle : title,
    headline: presentation.headline,
  };
};

export const activityHasDetail = (message) => {
  if (getTaskLogRef(message)) return false;
  const presentation = getActivityPresentation(message);
  if (presentation.extra || presentation.origin) return true;
  if (isLegacyXproLog(message)) return true;
  const detail = message?.logMeta?.detail;
  if (detail && typeof detail === "object" && Object.keys(detail).length) return true;
  if (getActivityChanges(message).length > 1) return true;
  const entity = message?.logMeta?.entity;
  if (entity?.href) return true;
  const actions = message?.logMeta?.actionsSummary;
  if (Array.isArray(actions) && actions.length > 2) return true;
  return false;
};

const looksLikeAiTypeLog = (message) => {
  if (message?.logMeta?.code === "ai_type_classification") return true;
  const plain = messagePlain(message);
  return /pelo sistema\s*\(\s*ia\s*\)/i.test(plain);
};

export const getActivityActor = (message, loggedUser) => {
  let kind = message?.logMeta?.actorKind || "";
  const rawName = message?.logMeta?.actorUserName || message?.senderName || message?.userName || "";
  const actorId = message?.logMeta?.actorUserId || message?.senderId || message?.id_usuario;
  if (!kind) {
    if (looksLikeAiTypeLog(message)) kind = "ai_agent";
    else if (Number(actorId) > 0 || (rawName && !/^(sistema|automação|automacao)$/i.test(rawName))) kind = "user";
    else kind = "system";
  }
  if (looksLikeAiTypeLog(message) && (!kind || kind === "system")) kind = "ai_agent";
  let label = getLogActorDisplayName(message, loggedUser) || "";
  if (kind === "ai_agent" && (!label || /^sistema$/i.test(label))) label = "IA";
  if (!label) label = kind === "user" ? "Atendente" : "Sistema";
  return {
    kind,
    label,
    deleted: Boolean(message?.logMeta?.actorDeleted),
  };
};

const actorKey = (message) => {
  const kind = message?.logMeta?.actorKind || "";
  const id = message?.logMeta?.actorUserId || message?.senderId || "";
  const name = message?.logMeta?.actorUserName || message?.senderName || "";
  return `${kind}:${id || name}`;
};

export const getGroupActorLabel = (messages, loggedUser) => {
  const keys = new Set((messages || []).map(actorKey));
  if (keys.size <= 1) return getActivityActor(messages?.[0], loggedUser);
  const labels = [];
  const seen = new Set();
  for (const message of messages) {
    const actor = getActivityActor(message, loggedUser);
    if (seen.has(actor.label)) continue;
    seen.add(actor.label);
    labels.push(actor.label);
  }
  if (labels.length <= 3) {
    return { kind: "mixed", label: labels.join(", "), deleted: false };
  }
  return { kind: "mixed", label: `${labels.length} pessoas`, deleted: false };
};

export const distinctActivityPreview = (messages, loggedUser) => {
  const sentences = [];
  const seen = new Set();
  for (const message of messages || []) {
    const sentence = getActivitySentence(message, loggedUser);
    if (!sentence || seen.has(sentence)) continue;
    seen.add(sentence);
    sentences.push(sentence);
  }
  return sentences.slice(0, 3).join(" · ");
};

export const collapseIdenticalLogs = (messages, loggedUser) => {
  const rows = [];
  for (const message of messages || []) {
    const sentence = getActivitySentence(message, loggedUser);
    const minute = formatActivityClock(getMessageDate(message));
    const previous = rows[rows.length - 1];
    if (previous && previous.sentence === sentence && previous.minute === minute) {
      previous.repeat += 1;
      previous.messages.push(message);
      continue;
    }
    rows.push({ message, messages: [message], sentence, minute, repeat: 1 });
  }
  return rows;
};

export const formatConfidence = (confidence) => {
  if (!confidence || typeof confidence !== "object") return "";
  return Object.entries(confidence)
    .filter(([, value]) => value != null && value !== "")
    .map(([key, value]) => {
      const n = Number(value);
      const shown = Number.isFinite(n) ? n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(value);
      return `${key} ${shown}`;
    })
    .join(", ");
};
